const PictViewClass = require('pict-view');

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

class ObservationCreateMedia extends PictViewClass
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.FileListToUpload = []; // the list of files to upload, accessible across the component

		// the default options for the component
		this.DefaultConfiguration = {
			ObservationTypes: [ 'Legacy Image', 'Legacy Video', 'Legacy Audio', 'File' ],
			TagTypes: [ 'Tag' ], // TODO: add more types
			TagsList: [], // TODO: replace with types later
			FileSizeLimit: 4000000000, // 4 GB
			FileNumberLimit: 20,
			AllowCamera: true,
			AllowLocationRequest: true,
			FileRestrictions: [],
			DocumentInjectionTarget: null, // passed by method to target a specific element
			DocumentInjectionMethod: 'append', // replace, append, prepend,
			DocumentPreviewIdentifierAttribute: 'data-observation-id', // this is the unique attribute the template agrees to use to locate the preview image for each observation id
			onAfterDocumentJoinCallback: () =>
			{
				// optional callback to run after document join
			},
			onAfterCallback: (observationInfos, event) =>
			{
				// NOTE: You should override this method via configuration to do anything here besides close the panel and clear the banner after an upload action.
				// EX: Observation Image Sets will set this callback to inject the new images into the specific set location on the DOM.
				this.pict.log.info('No custom onAfterCallback specified in configuration for ObservationCreateMedia panel.');
			},
			onFallbackCallback: (observationInfoList) =>
			{
				// NOTE: this override does NOT get overwritten when the panel closes. You should use this when you need to do something on your view whenever a file is uploaded.
				// Example: show uploaded photo as inspection photo on bridge form after upload.
				this.pict.log.info('No custom onFallbackCallback specified in configuration for ObservationCreateMedia panel.');
			},
		};

		// add a pattern to the template engine to convert bytes to megabytes
		this.pict.MetaTemplate.addPattern('{~ToMegaBytes:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpColumnData = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return tmpColumnData ? this.convertBytesToMegabytes(tmpColumnData) : 0;
		});
		this.pict.MetaTemplate.addPattern('{~ToGigaBytes:', '~}', (pHash, pData, fCallback) =>
		{
			const tmpColumnData = this.pict.resolveStateFromAddress(pHash, pData, fCallback);
			return tmpColumnData ? this.convertBytesToGigabytes(tmpColumnData) : 0;
		});
	}

	instantiateConfiguration()
	{
		this.log.info('Initializing ObservationCreateMedia view...');
		if (!this.InstanceConfiguration)
		{
			this.DefaultConfiguration.IDDocument = this.AppData?.DocumentData?.ReportData?.IDDocument;
			this.DefaultConfiguration.IDProject = this.AppData?.DocumentData?.ReportData?.IDProject;
			this.DefaultConfiguration.IDCustomer = this.AppData?.DocumentData?.ReportData?.IDCustomer || this.AppData?.CurrentUser?.CustomerID;
			// the instance configuration for the component (the devloper can mutate this)
			this.InstanceConfiguration = JSON.parse(JSON.stringify(this.DefaultConfiguration));
		}
	}

	/**
	 * This method is used to build the tag selection list for the user to choose from as an observation tag.
	 * This is useful for the user to select from a list of tags that are relevant to the project or customer.
	 * @param {String} pFileGUID - the GUID of the file to build the tag selection list for
	 * @return {void}
	 */
	lookUpTagsForSelectionList(pFileGUID)
	{
		// if we have tags already, use them
		if (this.InstanceConfiguration.TagsList.length > 0)
		{
			this.buildTagSelectionList(pFileGUID);
			return;
		}
		this.log.info('Looking up tags for the selection list...');
		// get the tags from the server
		let tmpUrl = 'ObservationTags/FilteredTo/FBV~TagType~EQ~Tag';
		// if we have a project, filter the tags by the project
		if (this.InstanceConfiguration.IDProject)
		{
			tmpUrl = `ObservationTagProjectJoins/FilteredTo/FBV~IDProject~EQ~${ this.InstanceConfiguration.IDProject }`;
		}
		this.pict.HeadlightRestClient.getJSON(tmpUrl,
		(pError, pBody) =>
		{
			if (pError)
			{
				this.pict.log.error('Failed to retrieve tags for selection list.', pError);
				this.pict.views.HeadlightPanels.toast({
					message: `<strong>Failed to retrieve tags for selection list.</strong><br/><em>${pError.message || pError}</em>`,
					type: 'is-danger',
				});
				this.buildTagSelectionList(pFileGUID);
				return;
			}
			if (pBody && Array.isArray(pBody))
			{
				this.InstanceConfiguration.TagsList = pBody;
				this.buildTagSelectionList(pFileGUID);
			}
		});
	}

	/**
	 * This method is to build the tag selection list for the user to choose from as an observation tag.
	 * This is useful for the user to select from a list of tags that are relevant to the project or customer.
	 * @param {String} pFileGUID
	 */
	buildTagSelectionList(pFileGUID)
	{
		const tmpTagList = this.InstanceConfiguration.TagsList;
		// list the tags by name/id for the selection list
		const elementId = '#ObservationCreateMedia-FileList-LabelSelect';
		// pretty up the Tag selector
		if (window.jQuery)
		{
			if (this.pict.ContentAssignment.hasClass(elementId, 'select2-hidden-accessible'))
			{
				window.jQuery(elementId).select2('destroy');
			}
			const tmpSelect2Data = [];
			let tmpSelectedTags = [];
			// if we have a file that already has tags set (multiple files), get the tags from the file
			if (pFileGUID)
			{
				const tmpFile = this.findFileByGUID(pFileGUID);
				if (tmpFile && tmpFile.Tags && tmpFile.Tags.length > 0)
				{
					tmpSelectedTags = tmpFile.Tags;
				}
			}

			// clear out any active selections from the select2 dropdown so they don't double pick
			for (let i=0; i < tmpTagList.length; i++)
			{
				tmpSelect2Data.push({
					id: tmpTagList[i].Tag,
					text: tmpTagList[i].Tag,
					selected: tmpSelectedTags.includes(tmpTagList[i].Tag), // NOTE: Could be an issue if we have two tags with the same name (but that shouldn't be allowed)
				});
			}
			let placeholder = 'Select tags...';
			// if we have no tags, show a message
			if (tmpSelect2Data.length === 0)
			{
				placeholder = 'No tags available.';
			}
			window.jQuery(elementId).html('').select2({
				dropdownParent: $('#ObservationCreateMedia-Content-Entry'),
				placeholder: placeholder,
				width: '100%',
				multiple: true,
				data: tmpSelect2Data,
				tags: true,
				dropdownCssClass: 'theme-dark',
				createTag: function(params)
				{
					const term = params.term.trim();
					if (term === '')
					{
						return null;
					}
					return {
						id: term,
						text: term,
						newTag: true,
					};
				},
			});
		}
	}

	/**
	 * Basic utility method to convert bytes to megabytes.
	 * @param {Number} pBytes
	 * @return {Number}
	 */
	convertBytesToMegabytes(pBytes)
	{
		return Math.round(pBytes / 1000000);
	}

	/**
	 * Basic utility method to convert bytes to gigabytes.
	 * @param {Number} pBytes
	 * @return {Number}
	 */
	convertBytesToGigabytes(pBytes)
	{
		return Math.round(pBytes / 1000000000);
	}

	/**
	 * This is a utility method to find a file in the list by the GUID.
	 * @param {String} pFileGUID
	 * @return {Object}
	 */
	findFileByGUID(pFileGUID)
	{
		let tmpFile = null;
		// find the file in our list by the guid string
		this.FileListToUpload.forEach((pFile, i) =>
		{
			if (pFile.GUID === pFileGUID)
			{
				tmpFile = pFile;
				return;
			}
		});
		return tmpFile;
	}

	/**
	 * This method is used to check if the document is locked before uploading files.
	 * If the document is locked, it will prevent the upload and show a message to the user.
	 * @param {Event} e - the event object
	 */
	checkIfLocked(e)
	{
		if (this.AppData?.DocumentData?.ReportData?.Locked)
		{
			e.preventDefault();
			this.pict.views.HeadlightPanels.toast({
				message: 'The document is locked and cannot be modified.',
				type: 'is-danger',
			});
		}
	}
	/**
	 * This method is used to update the selection list to show the user which file they have selected for preview/editing.
	 * @param {Object} [pQuickCaptureFile] - a file object from the quick capture event, which bypasses the file picker
	 */
	handleUpdateSelectionList(pQuickCaptureFile)
	{
		// warn developers to migrate to the new event system
		if (pQuickCaptureFile)
		{
			this.pict.log.warn('Use the dedicated quick capture method to add files. See "quickCaptureImage()".');
			return;
		}
		this.log.info('Updating file selection list...');
		this.updateStepsListActiveState(1);
		const filesInput = document.getElementById('ObservationCreateMedia-FileList-Input');
		const tmpFileList = filesInput instanceof HTMLInputElement && filesInput.files;
		const tmpIncludedFileList = [];
		const tmpExcludedFileList = [];
		// clear out the list on the UI everytime
		this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-FileList-SelectionList', '');
		// show the empty row message
		this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-FileList-EmptyRow', 'is-hidden');

		// prevent more files than the limit (if set)
		if (this.InstanceConfiguration.FileNumberLimit && tmpFileList.length > this.InstanceConfiguration.FileNumberLimit)
		{
			this.pict.log.warn('File number limit exceeded! Limit: ', this.InstanceConfiguration.FileNumberLimit, ' Selected: ', tmpFileList.length);
			// inject message on the file picker
			this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-FileList-ErrorMessage', /*html*/`
			<div class="column notification is-danger is-dark mb-3 is-size-7">The maximum limit of files allowed for a single upload is set to ${ this.InstanceConfiguration.FileNumberLimit }. Please try again with fewer files selected.</div>`);
			return;
		}
		else
		{
			this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-FileList-ErrorMessage', '');
		}

		// if we have files, then process them
		if (tmpFileList && tmpFileList.length > 0)
		{
			// disable the button until they reset the list
			this.pict.ContentAssignment.setAttribute('#ObservationCreateMedia-FileList-CreateButton', 'disabled', 'disabled');
			this.pict.ContentAssignment.removeAttribute('#ObservationCreateMedia-FileList-ResetButton', 'disabled');
			// hide the empty row
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-FileList-EmptyRow', 'is-hidden');
			this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-FileList-EmptyRow', 'is-active');

			// for each file, generate a UI element
			for (let i = 0; i < tmpFileList.length; i++)
			{
				// limit file size (if set)
				if (this.InstanceConfiguration.FileSizeLimit && tmpFileList[i].size > this.InstanceConfiguration.FileSizeLimit)
				{
					this.pict.log.warn('File size limit exceeded! Limit: ', this.InstanceConfiguration.FileSizeLimit, ' Selected: ', tmpFileList[i].size);
					tmpExcludedFileList.push(tmpFileList[i]);
				}
				else
				{
					const tmpFile = tmpFileList[i];
					// formatted object to store in the list
					const tmpFileDetails = {
						Name: tmpFile.name, // can be updated by the user - becomes the 'Observation Name'
						FileName: tmpFile.name, // for display purposes only
						Description: '', // can be updated by the user - becomes the 'Observation Description'
						Tags: [], // can be updated by the user - becomes the 'Observation Tags'
						File: tmpFile, // the original file object in its entirety
						FileTypeIcon: this.pict.providers.ObservationProvider.returnFileIconFromFileType(tmpFile.type), // the icon to display for the file type
						ObservationType: this.pict.providers.ObservationProvider.returnObservationTypeFromFileType(tmpFile.type), // the type of observation to create
						GUID: this.pict.getUUID(), // a unique identifier for the file
					};
					// store this in the 'valid' list to be set to the global list after the loop
					tmpIncludedFileList.push(tmpFileDetails);
					// add the file list item to the page
					this.render('ObservationCreateMedia-Renderable-SelectionListRow', '#ObservationCreateMedia-FileList-SelectionList', tmpFileDetails);
				}
			}

			// now show the error message if we have any excluded files
			if (tmpExcludedFileList.length)
			{
				let tmpUIList = '';
				for (let i = 0; i < tmpExcludedFileList.length; i++)
				{
					tmpUIList += `<li>${ tmpExcludedFileList[i].name }</li>`;
				}
				// inject message above the file list
				this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-FileList-ErrorMessage', /*html*/`
				<div class="column notification is-danger is-dark mb-3 is-size-7">
					<span>The following files exceed the max individual file size limit of ${ this.convertBytesToGigabytes(this.InstanceConfiguration.FileSizeLimit) } GB:</span>
					<ol class="pl-5">${ tmpUIList }</ol>
				</div>`);
			}
		}
		const newCount = tmpIncludedFileList.length + '';
		// update the count in the header
		this.pict.ContentAssignment.assignContent('#FilesToUploadCount', newCount);
		// store our local copy for the next step
		this.FileListToUpload = tmpIncludedFileList;
		this.handleUploadButton();

		// set the first row as active
		if (this.FileListToUpload.length > 0)
		{
			const button = document.querySelector('#ObservationCreateMedia-FileList-SelectionList .panel-block:first-child');
			if (button instanceof HTMLElement)
			{
				button.click();
			}
		}
	}

	/**
	 * This method is used to handle the upload button state based on the file list.
	 * If there are files in the list, the button is enabled, otherwise it is disabled.
	 * This is useful for the user to know when they can upload the files.
	 */
	handleUploadButton()
	{
		if ( this.FileListToUpload.length > 0)
		{
			this.pict.ContentAssignment.removeAttribute('#ObservationCreateMedia-FileList-UploadButton', 'disabled');
		}
		else
		{
			this.pict.ContentAssignment.setAttribute('#ObservationCreateMedia-FileList-UploadButton', 'disabled', 'disabled');
		}
	}

	/**
	 * This method is used to handle the quick capture image event. This does everything from
	 * capturing the image, to uploading it to the server, and then updating the UI, but hides
	 * the details from the user. This is limited to single file only and does not use the upload panel flow.
	 * @param {Object} pFileInput
	 * @param {Object} pConfiguration
	 * @param {Function} pCallback
	 */
	async quickCaptureImage(pFileInput, pConfiguration, pCallback)
	{
		this.pict.log.info('Quick capture image: ', pConfiguration);

		if (!(pFileInput instanceof HTMLInputElement) || !pFileInput.files || pFileInput.files.length === 0)
		{
			this.pict.log.warn('No files selected for quick capture.');
			return;
		}
		else if (pFileInput.files.length > 1)
		{
			this.pict.log.warn('Multiple files selected for quick capture. Only the first file will be used.');
		}
		// set the button to "loading" since we have no other UI to show the user
		if (window.jQuery)
		{
			window.jQuery(pFileInput).parents('.button').addClass('is-loading');
		}
		// if we passed a callback set it now before we set the configuration
		// NOTE: this will run after the uploads are completed
		if (typeof pCallback === 'function')
		{
			this.InstanceConfiguration.onAfterCallback = pCallback;
		}
		this.setInstanceConfiguration(pConfiguration);

		const tmpFile = pFileInput.files[0];
		// formatted object to store in the list
		const tmpFileDetails = {
			Name: tmpFile.name, // can be updated by the user - becomes the 'Observation Name'
			FileName: tmpFile.name, // for display purposes only
			File: tmpFile, // the original file object in its entirety
			ObservationType: this.pict.providers.ObservationProvider.returnObservationTypeFromFileType(tmpFile.type), // the type of observation to create
			GUID: this.pict.getUUID(), // a unique identifier for the file
		};
		this.FileListToUpload = [ tmpFileDetails ];

		this.pict.log.info('Uploading file via Quick Capture: ', this.FileListToUpload);

		const observationResultList = [];

		// for each file, upload it and create an observation in a Promise
		await new Promise((resolve) =>
		{
			this.pict.providers.ObservationProvider.prepareObservationObjectFromFile(tmpFileDetails, this.InstanceConfiguration.IDProject,
			(observationResult) =>
			{
				// add the result to the list
				observationResultList.push(observationResult);
				// update the file object with the result so we can use it for the preview replacement
				tmpFileDetails._ObservationResult = observationResult;
				// join to the document, if we have one
				this.pict.providers.ObservationProvider.joinObservationToDocumentRecord(observationResult.IDObservation, this.InstanceConfiguration.IDDocument, async () =>
				{
					// add the new observation to the document list (these are 'included' observations)
					const tmpObservations = this.AppData?.DocumentData?.ReportData?.Observations;
					// add result to our document if we have one and it is not already in the observations list
					if (tmpObservations && Array.isArray(tmpObservations) && tmpObservations.findIndex((o) => o.IDObservation === observationResult.IDObservation) < 0)
					{
						tmpObservations.push(observationResult);
					}
					resolve(observationResult);
				});
			});
		});
		// if we have a callback, run it now
		//FIXME: it seems like we don't need a callback here? we can just return the result?
		if (typeof this.InstanceConfiguration.onAfterCallback == 'function')
		{
			this.log.info('Running onAfterCallback...', this.InstanceConfiguration.onAfterCallback);
			this.FileListToUpload = [];
			await this.InstanceConfiguration.onAfterCallback(observationResultList.map((o) =>
			{
				return { IDObservation: o.IDObservation, Observation: o, FileDetails: tmpFileDetails };
			}));
		}
		else
		{
			const lastObservation = observationResultList[observationResultList.length - 1];
			this.uploadViewFinished(true, lastObservation);
		}
		// finally, removing any loading state from the buttons
		this.pict.providers.UtilityProvider.removeAllLoadingClasses();
	}

	/**
	 * This method is used to reset the interim view content back to the initial state.
	 */
	resetIntermViewContent()
	{
		this.pict.ContentAssignment.removeAttribute('#ObservationCreateMedia-Content-Interim-ProgressBar', 'value');
		this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-Content-Interim-ProgressBar', 'is-success');
		this.pict.ContentAssignment.setAttribute('#ObservationCreateMedia-Content-Interim-Button', 'disabled', '');
		this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-Content-Interim-CurrentUploadCountMessage', 'File 0 of 0 completed.');
	}

	/**
	 * This method is used to reset the file selection list back to the initial state.
	 * This is useful if the user wants to start over or if they have a file that is too large
	 * and they need to remove it from the list.
	 */
	resetFileSelectionList()
	{
		this.log.info('Resetting file selection list...');
		const tmpFileList = document.getElementById('ObservationCreateMedia-FileList-Input');
		// this may not be set if the user is using the quick capture method
		if (!tmpFileList || !(tmpFileList instanceof HTMLInputElement))
		{
			return;
		}
		// clear out the list on the UI everytime
		tmpFileList.value = '';
		// the re-run the selection flow
		this.handleUpdateSelectionList();
		// swap out the empty panel for the detail panel
		this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel', 'is-hidden');
		this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-DetailPanel-Empty', 'is-hidden');
		// enable the add button again, disable the reset button
		this.pict.ContentAssignment.removeAttribute('#ObservationCreateMedia-FileList-CreateButton', 'disabled');
		this.pict.ContentAssignment.setAttribute('#ObservationCreateMedia-FileList-ResetButton', 'disabled', 'disabled');
		this.updateStepsListActiveState(0);
		this.handleUploadButton();
	}

	/**
	 * This method is used to set the active row in the file selection list and show the details for the file.
	 * @param {String} pFileGUID
	 */
	setFileSelectionRowActive(pFileGUID)
	{
		const tmpFile = this.findFileByGUID(pFileGUID);

		if (!tmpFile)
		{
			this.pict.log.warn('File not found for GUID: ', pFileGUID);
			return;
		}

		// swap out the active row
		this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-FileList-SelectionList .panel-block', [ 'is-active', 'has-background-link-40' ]);
		this.pict.ContentAssignment.addClass(`#ObservationCreateMedia-FileList-SelectionList .panel-block[data-guid="${ pFileGUID }"]`, [ 'is-active', 'has-background-link-40' ]);
		// swap out the empty panel for the detail panel
		this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-DetailPanel', 'is-hidden');
		this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-Empty', 'is-hidden');
		// update the detail panel
		this.render('ObservationCreateMedia-Renderable-SelectionListDetails', ('#ObservationCreateMedia-DetailPanel'), tmpFile);
		// build the tag selection list
		this.lookUpTagsForSelectionList(pFileGUID);

		// if we have an image, show it in the preview
		if (tmpFile && tmpFile.File.type.includes('image'))
		{
			// update the image file preview
			const fileReader = new FileReader();
			const preview = document.querySelector('#ObservationCreateMedia-DetailPanel img');
			fileReader.onload = (event) =>
			{
				const result = event.target.result;
				if (typeof result === 'string')
				{
					preview.setAttribute('src', result);
				}
			};
			fileReader.readAsDataURL(tmpFile.File);
			// this file type is not an image, swap out the image preview for a placeholder
			this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-DetailPanel-Image', 'is-hidden');
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-FilePreview', 'is-hidden');
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-NoImage', 'is-hidden');
		}
		else if (tmpFile && tmpFile.File.type.includes('pdf'))
		{
			const fileReader = new FileReader();
			const preview = document.querySelector('#ObservationCreateMedia-DetailPanel iframe');
			fileReader.onload = (event) =>
			{
				const result = event.target.result;
				if (typeof result === 'string')
				{
					preview.setAttribute('src', result);
				}
				this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-DetailPanel-FilePreview', 'is-skeleton');
			};
			fileReader.readAsDataURL(tmpFile.File);
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-Image', 'is-hidden');
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-FilePreview', 'is-skeleton');
			this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-DetailPanel-FilePreview', 'is-hidden');
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-NoImage', 'is-hidden');
		}
		// files don't have a preview
		else
		{
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-Image', 'is-hidden');
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-DetailPanel-FilePreview', 'is-hidden');
			this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-DetailPanel-NoImage', 'is-hidden');
		}
	}

	/**
	 * This can be used by a developer to set the configuration for all instances if they
	 * prefer to set project/customer level defaults in the configuration file this is useful
	 * if you want to set the same configuration for all instances other than the target
	 * project/customer.
	 * @param {Object} pConfiguration
	 */
	setInstanceConfiguration(pConfiguration)
	{
		this.InstanceConfiguration = { ...this.InstanceConfiguration, ...pConfiguration };
	}

	/**
	 * This method is used to update the steps list to show the user where they are in the process.
	 */
	toggleContentViews()
	{
		if (this.pict.ContentAssignment.hasClass('#ObservationCreateMedia-Content-Entry', 'is-hidden'))
		{
			this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-Content-Entry', 'is-hidden');
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-Content-Interim', 'is-hidden');
		}
		else
		{
			this.pict.ContentAssignment.addClass('#ObservationCreateMedia-Content-Entry', 'is-hidden');
			this.pict.ContentAssignment.removeClass('#ObservationCreateMedia-Content-Interim', 'is-hidden');
		}
	}

	/**
	 * This method is used to update the title of Observation that will be created.
	 * @param {String} pFileGUID
	 */
	updateFileTitle(pFileGUID)
	{
		const tmpFile = this.findFileByGUID(pFileGUID);
		tmpFile.Name = this.pict.ContentAssignment.readContent('#ObservationCreateMedia-DetailPanel input');
	}

	/**
	 * This method is used to update the description of the Observation that will be created.
	 * @param {String} pFileGUID
	 */
	updateFileDescription(pFileGUID)
	{
		const tmpFile = this.findFileByGUID(pFileGUID);
		tmpFile.Description = this.pict.ContentAssignment.readContent('#ObservationCreateMedia-DetailPanel textarea');
	}

	/**
	 * This method is used to update the tags of the Observation that will be created.
	 * @param {String} pFileGUID
	 */
	updateFileLabels(pFileGUID)
	{
		const tmpFile = this.findFileByGUID(pFileGUID);
		tmpFile.Tags = this.pict.ContentAssignment.readContent('#ObservationCreateMedia-DetailPanel select');
	}

	/**
	 * This method is used to update the interim view content to show the user that the upload is complete.
	 */
	updateIntermViewContent()
	{
		this.pict.ContentAssignment.setAttribute('#ObservationCreateMedia-Content-Interim-ProgressBar', 'value', '100');
		this.pict.ContentAssignment.addClass('#ObservationCreateMedia-Content-Interim-ProgressBar', 'is-success');
		this.pict.ContentAssignment.removeAttribute('#ObservationCreateMedia-Content-Interim-Button', 'disabled');
		this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-Content-Interim-CurrentUploadCountMessage', 'Upload complete!');
	}

	/**
	 * This method is used to upload the selected files to the server and create the observations. Optional
	 * callback can be passed to run after the upload is complete.
	 */
	async uploadSelectedFiles()
	{
		// update the UI to show the user we are uploading
		this.updateStepsListActiveState(2);
		// switch to the interim content
		this.toggleContentViews();

		this.pict.log.info('Uploading files: ', this.FileListToUpload);

		const observationResultList = [];
		const observationInfoList = [];

		// for each file, upload it and create an observation in a Promise
		const uploadPromises = this.FileListToUpload.map((pFile, i) =>
		{
			return new Promise((resolve, reject) =>
			{
				// update the current file count
				this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-Content-Interim-CurrentUploadCountMessage', `Uploading File ${ i + 1 } of ${ this.FileListToUpload.length }.`);
				this.pict.ContentAssignment.setAttribute('#ObservationCreateMedia-Content-Interim-ProgressBar', 'value', '0');
				this.pict.providers.ObservationProvider.prepareObservationObjectFromFile(pFile, this.InstanceConfiguration.IDProject,
				(observationResult) =>
				{
					// add the result to the list
					observationResultList.push(observationResult);
					observationInfoList.push({ IDObservation: observationResult.IDObservation, Observation: observationResult, FileDetails: pFile });
					// update the file object with the result so we can use it for the preview replacement
					pFile._ObservationResult = observationResult;
					// join to the document, if we have one
					this.pict.providers.ObservationProvider.joinObservationToDocumentRecord(observationResult.IDObservation, this.InstanceConfiguration.IDDocument, async () =>
					{
						// add the new observation to the document list (these are 'included' observations)
						const tmpObservations = this.AppData?.DocumentData?.ReportData?.Observations;
						// add result to our document if we have one (and only if it is not already added to the document)
						if (tmpObservations && Array.isArray(tmpObservations) && !tmpObservations.map((o) => o.IDObservation).includes(observationResult.IDObservation))
						{
							tmpObservations.push(observationResult);
						}
						// if an observation set is active, add the observation to the set too
						if (this.pict.views.ObservationSet.ActiveObservationSet)
						{
							this.pict.views.ObservationSet.addObservationToSet(observationResult.IDObservation);
						}
						// if we have a post-join callback, then run it now
						if (this.InstanceConfiguration.onAfterDocumentJoinCallback && typeof this.InstanceConfiguration.onAfterDocumentJoinCallback === 'function')
						{
							this.InstanceConfiguration.onAfterDocumentJoinCallback(observationResult);
						}
						resolve(observationResult);
					});
				},
				(pctComplete) =>
				{
					// update the progress bar's value
					const tmpValue = pctComplete * 100 + '';
					this.pict.ContentAssignment.setAttribute('#ObservationCreateMedia-Content-Interim-ProgressBar', 'value', tmpValue);
				});
			});
		});
		// wait for all the promises to resolve — use allSettled so one failed
		// upload doesn't prevent the rest from completing or skip cleanup
		const uploadResults = await Promise.allSettled(uploadPromises);
		const failedUploads = uploadResults.filter((r) => r.status === 'rejected');
		if (failedUploads.length > 0)
		{
			this.pict.log.error(`${failedUploads.length} of ${uploadResults.length} file(s) failed to upload:`, failedUploads);
			this.pict.views.HeadlightPanels.toast({
				message: `${failedUploads.length} of ${uploadResults.length} file(s) failed to upload. Check the console for details.`,
				type: 'is-danger',
			});
		}
		this.pict.log.info('All files uploaded: ', observationResultList);
		this.FileListToUpload = [];
		// if we have a callback, run it now
		if (typeof this.InstanceConfiguration.onAfterCallback == 'function')
		{
			//reset the tag list
			this.resetObservationTagList();
			this.log.info('Running onAfterCallback...', this.InstanceConfiguration.onAfterCallback);
			await this.InstanceConfiguration.onAfterCallback(observationInfoList);
		}
		else
		{
			if (typeof this.InstanceConfiguration.onFallbackCallback == 'function')
			{
				this.log.info('Running onFallbackCallback...', this.InstanceConfiguration.onFallbackCallback);
				await this.InstanceConfiguration.onFallbackCallback(observationInfoList);
			}
			// update the UI to say the view is done (it's safe to close now)
			this.updateStepsListActiveState(3);
			this.updateIntermViewContent();
			// for the upload complete message
			const delayInSeconds = 3;
			// finally, let the user know we are done, then close the view for them if they don't
			for (let i = 0; i < delayInSeconds; i++)
			{
				this.pict.ContentAssignment.assignContent('#ObservationCreateMedia-Content-Interim-CurrentUploadCountMessage', `<p class="p-2 is-size-5">Upload complete!<p><p class="p-2 has-text-primary-light">Closing in ${ delayInSeconds - i }...</p>`);
				await new Promise((resolve) => setTimeout(resolve, 1000));
			}
		}
		// clean up the panel upload view and run any cache busting for media section content
		const lastObservation = observationResultList[observationResultList.length - 1];
		this.uploadViewFinished(false, lastObservation);
		// close the observation panel
		this.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'close');
		// clear the observation panel target and the banner on the header
		this.pict.views.HeadlightPaneObservations.clearObservationPanelTarget();
		// finally, removing any loading state from the buttons
		this.pict.providers.UtilityProvider.removeAllLoadingClasses();
	}

	/**
	 * This method is used to update the active state of the steps list in the header.
	 * @param {Number} pStepNumber
	 */
	updateStepsListActiveState(pStepNumber)
	{
		const steps = document.querySelectorAll('#ObservationCreateMedia-Steps-List li');
		steps.forEach((step, i) =>
		{
			if (i === pStepNumber)
			{
				step.classList.add('is-active');
			}
			else
			{
				step.classList.remove('is-active');
			}
		});
	}

	/**
	 * Resets the tags list for the observation creation view to ensure that new tags are displayed.
	 */
	resetObservationTagList()
	{
		this.InstanceConfiguration.TagsList = [];
	}

	/**
	 * This method is used to handle the file selection event and update the UI accordingly.
	 * @param {boolean} [pQuickCapture=false] - if the file was captured via the quick capture method
	 * @param {Object} [pLastObservation] - the last observation created, if available
	 * @returns {void}
	 */
	uploadViewFinished(pQuickCapture, pLastObservation)
	{
		this.pict.log.info('Upload view completed!');
		this.resetIntermViewContent();
		this.toggleContentViews();
		this.resetFileSelectionList();
		this.resetObservationTagList();

		this.pict.views.ObservationIncluded.buildIncludedObservationsList(true);
		// if we used quick capture, we don't want to open the observation included panel
		if (!pQuickCapture)
		{
			// switch to the included tab
			this.pict.views.HeadlightPanels.handlePanelToggle('Observations', 'open', () =>
			{
				this.pict.views.HeadlightPanels.toggleTabContent('HeadlightPanels-ObservationPanel-Included', 'Observation');
				// toggle open the collapsed Image obersvation group
				// TODO: might want to check the file upload type and open the correct group, but the 99% use case is images
				this.pict.views.HeadlightPanels.toggleCollapsiblePanelBlockGroup('Legacy Image');
			});
		}
	}
}

module.exports = ObservationCreateMedia;

module.exports.default_configuration =
{
	ViewIdentifier: 'ObservationCreateMedia',
	DefaultRenderable: 'ObservationCreateMedia',
	DefaultDestinationAddress: '#ObservationCreateMedia-Container',
	Templates:
	[
		{
			Hash: 'ObservationCreateMedia-Template',
			Template: /*html*/`
			<div id="ObservationCreateMedia">
				<header class="has-background-text-15 p-4">
					<ol id="ObservationCreateMedia-Steps-List" class="steps-list">
						<li class="is-active">Add Files</li>
						<li>Edit Details</li>
						<li>Upload Data</li>
						<li>Complete</li>
					</ol>
				</header>
				<!-- step 1: select files / edit details -->
				<div id="ObservationCreateMedia-Content-Entry" class="p-4">
					<div class="columns is-mobile">
						<div class="column is-one-third-tablet">{~T:ObservationCreateMedia-FileSelection-ListPanel~}</div>
						<div class="column has-text-white">
							<div class="notification has-text-centered p-6 empty-message" id="ObservationCreateMedia-DetailPanel-Empty">
								<span class="icon">
									<i class="fas fa-info-circle" aria-hidden="true"></i>
								</span>
								<span>No file preview available.</span>
							</div>
							<div id="ObservationCreateMedia-DetailPanel" class="is-hidden">
								{~T:ObservationCreateMedia-FileSelection-DetailPanel~}
							</div>
							<footer class="mt-4 buttons is-right">
								<button type="button" title="Upload selected file" id="ObservationCreateMedia-FileList-UploadButton" disabled class="button is-primary" onclick="_Pict.views.ObservationCreateMedia.uploadSelectedFiles()">
									<span class="icon"><i class="fas fa-fw fa-upload"></i></span>
									<span>Upload Files</span>
								</button>
							</footer>
						</div>
					</div>
				</div>
				<!-- step 2: upload files / results -->
				<div id="ObservationCreateMedia-Content-Interim" class="is-hidden p-4">
					<div class="columns is-mobile">
						<div class="column has-text-white">
							<p class="title is-6 has-text-centered mb-3 has-text-white" id="ObservationCreateMedia-Content-Interim-CurrentUploadCountMessage">File 0 of 0 completed.</p>
							<progress id="ObservationCreateMedia-Content-Interim-ProgressBar" class="progress is-primary" max="100"></progress>
							<div class="is-size-7 has-text-centered">This may take a few minutes depending on the size of the files and your internet connection.</div>
							<div class="is-size-7 has-text-centered">Please do not close this window until the upload is complete.</div>
						</div>
					</div>
				</div>
			</div>`,
		},
		{
			Hash: 'ObservationCreateMedia-FileSelection-ListRow',
			Template: /*html*/`
			<a class="panel-block has-background-text-20 mt-1" data-guid="{~D:Record.GUID~}" onclick="_Pict.views.ObservationCreateMedia.setFileSelectionRowActive('{~D:Record.GUID~}')">
				<span class="panel-icon">
					<i class="fas fa-fw {~D:Record.FileTypeIcon~}"></i>
				</span>
				<span class="is-clipped">{~D:Record.FileName~}</span>
			</a>`,
		},
		{
			Hash: 'ObservationCreateMedia-FileSelection-ListPanel',
			Template: /*html*/`
			<div id="ObservationCreateMedia-FileList-Limitations">
				<div class="column notification is-dark is-text mb-3">
					<p class="is-size-7"><strong>Upload Limitations:</strong></p>
					<p class="is-size-7">File Count: <span class="has-text-text-90">{~D:Context[0].InstanceConfiguration.FileNumberLimit~}</span></p>
					<p class="is-size-7">Individual File Size: <span class="has-text-text-90">{~ToGigaBytes:Context[0].InstanceConfiguration.FileSizeLimit~} GB</span></p>
					<p class="is-size-7 mt-2"><i class="fa fa-fw fa-info-circle"></i> You can hold "Shift" or "Control" to select multiple files.</p>
				</div>
			</div>
			<div id="ObservationCreateMedia-FileList-ErrorMessage"><!-- error message - reserved space --></div>
			<article class="is-primary">
				<p class="tag is-dark is-rounded is-pulled-right mt-3 mr-3" id="FilesToUploadCount">0</p>
				<p class="panel-heading is-size-6 has-background-text-15">Files to upload</p>
				<div class="overflow-list-container" id="ObservationCreateMedia-FileList-SelectionList" style="max-height: 50vh; overflow-y: auto;"></div>
				<div class="notification has-text-centered empty-message" style="border-top-left-radius: 0; border-top-right-radius: 0;" id="ObservationCreateMedia-FileList-EmptyRow">
					<p class="is-size-7 mt-3 mb-5">No files selected.</p>
					<label class="button p-0 is-primary is-fullwidth" 
					id="ObservationCreateMedia-FileList-CreateButton">
						<input 
							class="file-input" 
							type="file"
							id="ObservationCreateMedia-FileList-Input"
							name="file-list-to-upload"
							multiple
							onclick="_Pict.views.ObservationCreateMedia.checkIfLocked(event)"
							onchange="_Pict.views.ObservationCreateMedia.handleUpdateSelectionList()"
							/>
						<span class="file-cta" style="border:none">
							<span class="file-icon">
								<i class="fas fa-plus-circle"></i>
							</span>
							<span class="file-label is-hidden-mobile is-hidden-tablet-only"> Select files</span>
							<span class="file-label is-hidden-desktop"> Add</span>
						</span>
					</label>
				</div>
			</article>
			<div class="buttons is-right">
				<button 
					class="button is-primary is-dark mt-2" 
					id="ObservationCreateMedia-FileList-ResetButton"
					disabled 
					type="buton" 
					onclick="_Pict.views.ObservationCreateMedia.resetFileSelectionList()"
					>
					<span class="icon"><i class="fas fa-fw fa-undo"></i></span>
					<span>Reset list</span>
				</button>
			</div>`,
		},
		{
			Hash: 'ObservationCreateMedia-FileSelection-DetailPanel',
			Template: /*html*/`
			<div class="notification has-text-centered p-6 empty-message" id="ObservationCreateMedia-DetailPanel-NoImage">
				<span class="icon">
					<i class="fas fa-question-circle" aria-hidden="true"></i>
				</span>
				<span>No preview available for this file type.</span>
			</div>
			<figure class="image mb-4 is-hidden" id="ObservationCreateMedia-DetailPanel-FilePreview">
				<iframe class="preview-image has-background-text-15" style="width: 100%; height: 25vh; max-height: 350px; object-fit: contain;"></iframe>
			</figure>
			<figure class="image mb-4 is-hidden" id="ObservationCreateMedia-DetailPanel-Image">
				<img class="preview-image has-background-text-15" style="max-width: 100%; height: 25vh; max-height: 350px; object-fit: contain;">
			</figure>
			<div class="field">
				<label class="label">Title</label>
				<div class="control">
					<input 
						class="input" 
						type="text" 
						placeholder="Title" 
						value="{~D:Record.Name~}"
						onchange="_Pict.views.ObservationCreateMedia.updateFileTitle('{~D:Record.GUID~}')"
						>
				</div>
			</div>
			<div class="field">
				<label class="label">Description</label>
				<div class="control">
					<textarea onchange="_Pict.views.ObservationCreateMedia.updateFileDescription('{~D:Record.GUID~}')"
						class="textarea shrunken-textarea" 
						style="min-height: 5em;"
						placeholder="Description">{~D:Record.Description~}</textarea>
				</div>
			</div>
			<div class="field">
				<label class="label">Tags</label>
				<div class="control">
					<div class="select is-fullwidth">
						<select 
							class="is-skeleton"
							multiple
							id="ObservationCreateMedia-FileList-LabelSelect"
							onchange="_Pict.views.ObservationCreateMedia.updateFileLabels('{~D:Record.GUID~}')">
							<option value="" selected>Select a tag</option>
						</select>
					</div>
				</div>
			</div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ObservationCreateMedia',
			TemplateHash: 'ObservationCreateMedia-Template',
			DestinationAddress: '#ObservationCreateMedia-Container',
		},
		{
			RenderableHash: 'ObservationCreateMedia-Renderable-SelectionListRow',
			TemplateHash: 'ObservationCreateMedia-FileSelection-ListRow',
			RenderMethod: 'append',
		},
		{
			RenderableHash: 'ObservationCreateMedia-Renderable-SelectionListDetails',
			TemplateHash: 'ObservationCreateMedia-FileSelection-DetailPanel',
			RenderMethod: 'replace',
		},
	],
};
